import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { sendTrialEndingEmail, sendSubscriptionConfirmationEmail } from '../lib/email';

interface SubscriptionTier {
  id: string;
  name: string;
  features: string[];
}

interface UserSubscription {
  id: string;
  tier_id: string;
  active: boolean;
  expires_at: string | null;
  created_at: string;
  tier: SubscriptionTier;
}

interface StripeSubscription {
  customer_id: string;
  subscription_id: string | null;
  subscription_status: string;
  price_id: string | null;
  current_period_start: number | null;
  current_period_end: number | null;
  cancel_at_period_end: boolean;
  payment_method_brand: string | null;
  payment_method_last4: string | null;
}

export function useSubscription() {
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [stripeSubscription, setStripeSubscription] = useState<StripeSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sermonCount, setSermonCount] = useState(0);
  const [analysisCount, setAnalysisCount] = useState(0);
  const [trialEndsAt, setTrialEndsAt] = useState<Date | null>(null);
  const [isInTrial, setIsInTrial] = useState(false);
  const [daysLeftInTrial, setDaysLeftInTrial] = useState(0);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [hasLifetimeLicense, setHasLifetimeLicense] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCollaborator, setIsCollaborator] = useState(false);
  const [isSeriesAccess, setIsSeriesAccess] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadSubscriptionData = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data: { user }, error: userError } = await supabase.auth.getUser();

        if (userError) {
          console.error('Error getting user:', userError);
          return;
        }

        if (!user) {
          setLoading(false);
          return;
        }

        // Get user subscription data from user_subscriptions table
        const { data: subscriptionData, error: subscriptionError } = await supabase
          .from('user_subscriptions')
          .select(`
            *,
            tier:subscription_tiers(*)
          `)
          .eq('user_id', user.id)
          .maybeSingle();

        if (subscriptionError && subscriptionError.code !== 'PGRST116') {
          console.error('Error loading subscription:', subscriptionError);
          throw subscriptionError;
        }

        // Get Stripe customer and subscription data directly from tables
        const { data: stripeCustomer, error: customerError } = await supabase
          .from('stripe_customers')
          .select('customer_id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (customerError) {
          console.error('Error loading Stripe customer:', customerError);
        }

        let stripeSubData = null;
        if (stripeCustomer?.customer_id) {
          const { data: stripeSub, error: stripeSubError } = await supabase
            .from('stripe_subscriptions')
            .select(`
              customer_id,
              subscription_id,
              status,
              price_id,
              cancel_at_period_end,
              payment_method_brand,
              payment_method_last4
            `)
            .eq('customer_id', stripeCustomer.customer_id)
            .is('deleted_at', null)
            .order('created_at', { ascending: false })
            .maybeSingle();

          if (stripeSubError) {
            console.error('Error loading Stripe subscription:', stripeSubError);
          } else {
            stripeSubData = stripeSub;
          }
        }

        if (!mounted) return;

        // Check if user is admin
        const { data: adminData } = await supabase
          .from('admin_users')
          .select('role')
          .eq('user_id', user.id)
          .maybeSingle();

        const isAdmin = !!adminData;
        setIsAdmin(isAdmin);

        // Detect collaborator tier (auto-granted on team invite accept)
        const isCollaboratorTier = subscriptionData?.tier?.name === 'Collaborator';
        setIsCollaborator(isCollaboratorTier);

        // Detect Series Access add-on (standalone — Premium does not include this)
        const isSeriesAccessTier = subscriptionData?.tier?.name === 'Series Access';
        setIsSeriesAccess(isSeriesAccessTier);

        // Check if user has admin-granted access: active with no expiry (gifted) or a future expiry (extended trial)
        const hasFreePremiun = subscriptionData?.active &&
                              !isCollaboratorTier &&
                              (subscriptionData?.expires_at === null ||
                               new Date(subscriptionData.expires_at) > new Date());


        // Set subscription data
        setSubscription(subscriptionData);
        setStripeSubscription(stripeSubData);

        // Calculate trial period (14 days from user creation)
        const userCreatedAt = new Date(user.created_at);
        const trialEnd = new Date(userCreatedAt.getTime() + (14 * 24 * 60 * 60 * 1000));
        const now = new Date();

        // Check if Stripe subscription is active
        const isStripeActive = stripeSubData?.subscription_status === 'active' || 
                              stripeSubData?.subscription_status === 'trialing';

        // Check if subscription is suspended due to payment failure
        const isSuspended = stripeSubData?.subscription_status === 'past_due' || 
                           stripeSubData?.subscription_status === 'unpaid';

        // Check if user has lifetime license
        const isLifetime = stripeSubData?.subscription_status === 'completed';

        const hasActiveSubscription = isStripeActive || isLifetime;

        // Determine if user is in trial:
        // 1. Within 14 days of account creation AND
        // 2. No active paid subscription AND
        // 3. No admin-granted free premium AND
        // 4. Either no Stripe subscription, subscription status is 'not_started', or subscription is 'canceled'
        const hasNoActiveSubscription = !hasActiveSubscription;
        const isWithinTrialPeriod = now < trialEnd;
        const hasNotStartedOrCanceledSubscription = !stripeSubData || 
                                                   stripeSubData.status === 'not_started' || 
                                                   stripeSubData.status === 'canceled';

        const isCurrentlyInTrial = isWithinTrialPeriod && hasNoActiveSubscription && !hasFreePremiun && hasNotStartedOrCanceledSubscription;

        if (import.meta.env.DEV) {
          console.log('Trial calculation:', {
            userCreatedAt: userCreatedAt.toISOString(),
            trialEnd: trialEnd.toISOString(),
            now: now.toISOString(),
            isWithinTrialPeriod,
            hasNoActiveSubscription,
            hasNotStartedOrCanceledSubscription,
            stripeStatus: stripeSubData?.subscription_status,
            isCurrentlyInTrial,
            daysLeft: Math.max(0, Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
          });
        }

        setIsInTrial(isCurrentlyInTrial);
        setDaysLeftInTrial(Math.max(0, Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))));
        setTrialEndsAt(trialEnd);

        if (import.meta.env.DEV) {
          console.log('Setting trial state:', {
            isInTrial: isCurrentlyInTrial,
            daysLeftInTrial: Math.max(0, Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))),
            trialEndsAt: trialEnd
          });
        }

        setHasActiveSubscription(hasActiveSubscription);
        setHasLifetimeLicense(isLifetime);
      } catch (e) {
        console.error('Error loading subscription data:', e);
        setError(e instanceof Error ? e.message : 'Failed to load subscription');
      } finally {
        setLoading(false);
      }
    };

    loadSubscriptionData();

    return () => { mounted = false; };
  }, []);

  const hasFeature = (feature: string): boolean => {
    if (isAdmin) return true;

    // Collaborators have access to AI tools and shared sermon features
    if (isCollaborator) return true;

    const hasActiveStripeSubscription = stripeSubscription?.subscription_status === 'active' ||
                                       stripeSubscription?.subscription_status === 'trialing' ||
                                       stripeSubscription?.subscription_status === 'completed';

    const hasFreePremiun = subscription?.active &&
                           (subscription?.expires_at === null ||
                            new Date(subscription.expires_at) > new Date());

    if (isInTrial || hasActiveStripeSubscription || hasFreePremiun) return true;

    return false;
  };

  const canCreateSermon = (): boolean => {
    if (isAdmin) return true;
    // Collaborators cannot create their own sermons
    if (isCollaborator) return false;

    if (!subscription && !isInTrial) return false;

    const hasActiveStripeSubscription = stripeSubscription?.subscription_status === 'active' ||
                                       stripeSubscription?.subscription_status === 'trialing';

    const hasFreePremiun = subscription?.active &&
      (subscription?.expires_at === null || new Date(subscription.expires_at) > new Date());

    if (!isInTrial && !hasActiveStripeSubscription && !hasFreePremiun) return false;

    return true;
  };

  const canCreateAnalysis = (): boolean => {
    if (isAdmin) return true;
    // Collaborators can run AI analysis on shared sermons
    if (isCollaborator) return true;

    if (!subscription && !isInTrial) return false;

    const hasActiveStripeSubscription = stripeSubscription?.subscription_status === 'active' ||
                                       stripeSubscription?.subscription_status === 'trialing';

    const hasFreePremiun = subscription?.active &&
      (subscription?.expires_at === null || new Date(subscription.expires_at) > new Date());

    if (!isInTrial && !hasActiveStripeSubscription && !hasFreePremiun) return false;

    return true;
  };

  const getRemainingSermons = (): number => {
    // Check if user is admin - admins have unlimited access
    if (isAdmin) return Infinity;

    // Check subscription status - only active or trialing subscriptions are valid
    const hasActiveStripeSubscription = stripeSubscription?.subscription_status === 'active' ||
                                       stripeSubscription?.subscription_status === 'trialing';

    // Check for admin-granted free premium
    const hasFreePremiun = subscription?.active &&
      (subscription?.expires_at === null || new Date(subscription.expires_at) > new Date());

    if (isInTrial || hasActiveStripeSubscription || hasFreePremiun) return Infinity;
    return 0; // No access after trial without subscription
  };

  const getRemainingAnalyses = (): number => {
    // Check if user is admin - admins have unlimited access
    if (isAdmin) return Infinity;

    // Check subscription status - only active or trialing subscriptions are valid
    const hasActiveStripeSubscription = stripeSubscription?.subscription_status === 'active' ||
                                       stripeSubscription?.subscription_status === 'trialing';

    // Check for admin-granted free premium
    const hasFreePremiun = subscription?.active &&
      (subscription?.expires_at === null || new Date(subscription.expires_at) > new Date());

    if (isInTrial || hasActiveStripeSubscription || hasFreePremiun) return Infinity;
    return 0; // No access after trial without subscription
  };

  const upgradeToPremium = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user found');

      // Call the RPC function to upgrade to premium
      const { error } = await supabase.rpc('upgrade_to_premium', {
        user_uuid: user.id
      });

      if (error) throw error;

      // Get user profile for email
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('first_name')
        .eq('user_id', user.id)
        .single();

      if (profile) {
        // Send confirmation email
        await sendSubscriptionConfirmationEmail(
          user.email || '',
          profile.first_name,
          'Premium',
          'monthly',
          new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()
        );
      }

      // Reload subscription data
      await loadSubscription();
    } catch (e) {
      console.error('Error upgrading to premium:', e);
      setError(e instanceof Error ? e.message : 'Failed to upgrade to premium');
    } finally {
      setLoading(false);
    }
  };

  const loadSubscription = async () => {
    loadSubscriptionData();
  };

  return {
    subscription: subscription ? {
      ...subscription,
      isInTrial,
      daysLeftInTrial,
      trialEndsAt
    } : {
      isInTrial,
      daysLeftInTrial,
      trialEndsAt
    },
    stripeSubscription,
    setStripeSubscription,
    loading,
    error,
    hasFeature,
    canCreateSermon,
    canCreateAnalysis,
    getRemainingSermons,
    getRemainingAnalyses,
    upgradeToPremium,
    trialEndsAt,
    isInTrial,
    daysLeftInTrial,
    isCollaborator,
    isSeriesAccess,
    hasSeriesAccess: isAdmin || isSeriesAccess,
    refresh: loadSubscription
  };
}